import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { RetailerPhoneNumbersService } from 'app/_shared/services/api/retailerPhoneNumbers.service';
import { RetailerPhoneNumber, SearchRetailerPhoneNumbersRequest, Program, Retailer } from 'app/_shared/entities/entities';
import { ProgramsService } from 'app/_shared/services/api/programs.service';
import { RetailersService } from 'app/_shared/services/api/retailers.service';
import { lastValueFrom, Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

@Component({
    selector: 'app-retailer-phone-numbers-search',
    templateUrl: './retailer-phone-numbers-search.component.html',
    styleUrls: ['./retailer-phone-numbers-search.component.scss'],
    standalone: false
})
export class RetailerPhoneNumbersSearchComponent implements OnInit, AfterViewInit {
  filterForm: FormGroup;
  dataSource = new MatTableDataSource<RetailerPhoneNumber>([]);
  displayedColumns: string[] = [ // Cursor: never edit this array
    'retailerName',
    'programName',
    'callCenterPhoneNumberPhoneNumber',
    'active',
    'created',
    'createdBy',
    'lastModified',
    'lastModifiedBy'
  ];
  loading = false;
  error = false;
  errorMessage = '';
  totalRecords = 0;

  programs: Program[] = [];
  retailers: Retailer[] = [];
  filteredRetailers: Observable<Retailer[]>;
  retailerSearchControl = new FormControl('');

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private fb: FormBuilder,
    private service: RetailerPhoneNumbersService,
    private programsService: ProgramsService,
    private retailersService: RetailersService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.filterForm = this.fb.group({
      programId: [],
      active: [],
      retailerId: [],
      callCenterPhoneNumberId: [],
      createdFrom: [],
      createdTo: [],
      pageNumber: [1],
      pageSize: [25]
    });

  }

  async ngOnInit() {
    await this.loadPrograms();
    await this.loadRetailers();
    await this.retailersService.loadRetailers();
    await this.search(); // Initial search without sort

  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    // pagination is handled server side, so we don't need to set the paginator
  }

  async loadPrograms() {
    await this.programsService.loadPrograms();
    this.programsService.Programs.subscribe(listResponse => {
      this.programs = listResponse.records || [];
    });
  }

  async loadRetailers() {
    this.retailersService.Retailers.subscribe(retailers => {
      this.retailers = retailers.records;
      this.filteredRetailers = this.retailerSearchControl.valueChanges.pipe(
        startWith(''),
        map(value => this._filterRetailers(value))
      );
    });
  }

  private _filterRetailers(value: string | Retailer): Retailer[] {
    let filterValue = '';
    if (typeof value === 'string') {
      filterValue = value.toLowerCase();
    } else if (value && typeof value === 'object') {
      filterValue = value.name.toLowerCase();
    }
    return this.retailers.filter(retailer =>
      retailer.name.toLowerCase().includes(filterValue) ||
      retailer.retailerCode?.toLowerCase().includes(filterValue)
    );
  }

  // Display function for autocomplete
  displayRetailerFn(retailer?: Retailer): string {
    return retailer ? `${retailer.name}${retailer.retailerCode ? ' (' + retailer.retailerCode + ')' : ''}` : '';
  }

  onRetailerSelected(retailer: Retailer) {
    this.filterForm.patchValue({ retailerId: retailer ? retailer.retailerId : null });
  }

  /**
   * Triggers a search using the current filter form values and updates the table.
   * Handles loading and error states for Material UI feedback.
   */
  async search(): Promise<void> {
    this.loading = true;
    this.error = null;
    const request: SearchRetailerPhoneNumbersRequest = {
      ...this.filterForm.value,
      pageNumber: this.paginator ? this.paginator.pageIndex + 1 : 1,
      pageSize: this.paginator ? this.paginator.pageSize : 25,
      orderBy: this.sort?.active,
      orderAscending: this.sort?.direction === 'asc'
    };

    const result = await lastValueFrom(this.service.search(request));
    if (result.successful && result.data) {
      this.dataSource.data = result.data.records;
      this.totalRecords = result.data.totalRecords || 0;
      this.changeDetectorRef.detectChanges();
    } else {
      this.error = true;
      this.errorMessage = result.message || 'Search failed.';
    }
    this.loading = false;
  }

  /**
   * Handles pagination changes from the Material paginator.
   */
  onPaginateChange() {
    this.search();
  }

  /**
   * Handles sort changes from the Material sort header.
   * (Sorting is handled by MatSort, but you can implement server-side sorting if needed)
   */
  onSortChange(event: Sort) {
    console.debug('Sort changed:', event);
    this.paginator.pageIndex = 0; // Reset to first page on sort
    this.search();

  }

  /**
   * Clears all filters and resets pagination, then triggers a new search.
   */
  clearFilters() {
    this.filterForm.reset({ pageNumber: 1, pageSize: 25 });
    this.search();
  }

  getRetailerNameById(id: number): string {
    const retailer = this.retailers.find(r => r.retailerId === id);
    return retailer ? retailer.name : id.toString();
  }
}
